import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, AlertTriangle, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function Compliance() {
  return (
    <div className="min-h-screen bg-[#F9F7F3] py-12 px-6">
      <div className="max-w-4xl mx-auto">
        <Link to="/">
          <Button variant="ghost" size="sm" className="mb-8" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />Back
          </Button>
        </Link>

        <h1 className="text-4xl font-light tracking-tight mb-2" style={{fontFamily: 'Spectral, serif'}}>Trust & Community Standards</h1>
        <p className="text-lg text-[#737373] mb-12">Built for adults who want to stay connected with someone they care about.</p>

        <div className="space-y-6">
          <div className="bg-white border border-[#E5E0D8] rounded-md p-8 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
            <div className="flex items-start gap-4">
              <Heart className="w-8 h-8 text-[#B94A3E] flex-shrink-0" strokeWidth={1.5} />
              <div>
                <h2 className="text-2xl mb-4" style={{fontFamily: 'Spectral, serif'}}>A Tool for Connection</h2>
                <p className="text-[#1A1A1A]/85 leading-relaxed mb-4">
                  Stable Stationary helps you stay in touch with someone incarcerated — a partner, family member, friend, or someone you're building a relationship with.
                </p>
                <p className="text-[#1A1A1A]/85 leading-relaxed">
                  Whatever your reason for writing, we're here to help you show up more consistently. What you share and how your relationship grows is between you and them.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-[#E5E0D8] rounded-md p-8 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
            <div className="flex items-start gap-4">
              <Shield className="w-8 h-8 text-[#B94A3E] flex-shrink-0" strokeWidth={1.5} />
              <div>
                <h2 className="text-2xl mb-4" style={{fontFamily: 'Spectral, serif'}}>AI-Generated Media</h2>
                <ul className="space-y-2 text-[#1A1A1A]/85">
                  <li>• Every AI-generated photo is clearly labeled "Made with AI"</li>
                  <li>• AI-generated images are synthetic — they don't depict real events</li>
                  <li>• Only upload photos you own or have permission to use</li>
                  <li>• You control your gallery — nothing is shared without your action</li>
                </ul>
                <p className="text-sm text-[#737373] mt-4">
                  We label AI content so recipients can tell what's real and what's not. Beyond that, how you use the tool is up to you.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-[#E5E0D8] rounded-md p-8 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-8 h-8 text-[#B94A3E] flex-shrink-0" strokeWidth={1.5} />
              <div>
                <h2 className="text-2xl mb-4" style={{fontFamily: 'Spectral, serif'}}>Things We Don't Allow</h2>
                <p className="text-sm text-[#737373] mb-4">These rules exist to protect people from fraud and harm — not to police relationships.</p>
                <ul className="space-y-2 text-[#1A1A1A]/85">
                  <li>• <strong>Impersonating real people</strong> without their permission</li>
                  <li>• <strong>Fraud or scams</strong> that extract money or resources from vulnerable people</li>
                  <li>• <strong>Harassment, threats, or intimidation</strong> of anyone</li>
                  <li>• <strong>Content involving minors</strong> in any sexual or exploitative context</li>
                  <li>• <strong>Uploading photos of others</strong> without their permission</li>
                  <li>• <strong>Illegal activity</strong> of any kind</li>
                </ul>
                <p className="text-sm text-red-700 mt-4">
                  Violations may result in account termination. Otherwise, correspondence between consenting adults is your business.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-[#E5E0D8] rounded-md p-8 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
            <h2 className="text-2xl mb-4" style={{fontFamily: 'Spectral, serif'}}>Mail Delivery Reality</h2>
            <ul className="space-y-2 text-[#1A1A1A]/85">
              <li>• Facility rules affect what can be sent and received</li>
              <li>• Delivery timing depends on the facility's mail-processing — not on us</li>
              <li>• Some facilities restrict certain content types (photos, hardware, staples, etc.)</li>
              <li>• Check your recipient's facility rules before subscribing to avoid surprises</li>
              <li>• We do our best to format letters and photos to pass typical inspections</li>
            </ul>
          </div>

          <div className="bg-[#F5EBE0] border border-[#B94A3E]/20 rounded-md p-8">
            <h2 className="text-2xl mb-4" style={{fontFamily: 'Spectral, serif'}}>Our Promise</h2>
            <p className="text-[#1A1A1A]/85 leading-relaxed">
              We help you show up. Consistently. With warmth. In your own voice, in your own way. Two adults staying connected through mail is one of the oldest, most meaningful things people do — we're just making it a little easier.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
